import { GlobeIcon } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";

import {
  onOpenWorldBirthChooser,
  rememberPendingGovernedBirth,
} from "~/neucleos/worldBirthChooser";
import { worldDisplayName } from "~/neucleos/worldSelection";
import { enterWorld, useNeucleosWorldStore } from "~/neucleos/worldStore";
import { Dialog, DialogPopup } from "./ui/dialog";

/**
 * The one seat behind openWorldBirthChooser: the ALL stance's birth question.
 * Like Local asking which project folder, ALL asks which World the new work
 * will live in — the boundary that scopes it and homes its code and files.
 * Choosing a World remembers the pending birth and enters that World; its
 * governed ceremony host opens on arrival, so governance is never skipped.
 */
export function WorldBirthChooser(): ReactElement | null {
  const worlds = useNeucleosWorldStore((state) => state.worlds);
  const [open, setOpen] = useState(false);
  useEffect(() => onOpenWorldBirthChooser(() => setOpen(true)), []);
  if (worlds.length === 0) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPopup className="w-80">
        <div data-world-birth-chooser="">
          <p className="pb-1 text-sm font-medium text-foreground">Where will this work live?</p>
          <p className="pb-2 text-xs text-muted-foreground">
            A session is born into one World — the boundary that scopes it and homes its code and
            files.
          </p>
          <div className="flex flex-col gap-0.5" role="list">
            {worlds.map((world) => (
              <button
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                data-world-birth-choice={world.worldKey}
                key={world.worldKey}
                onClick={() => {
                  setOpen(false);
                  rememberPendingGovernedBirth(world.worldKey);
                  enterWorld(world.worldKey);
                }}
                type="button"
              >
                <GlobeIcon aria-hidden className="size-3.5 shrink-0" />
                <span className="min-w-0 flex-1 truncate">
                  {worldDisplayName(worlds, world.worldKey)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </DialogPopup>
    </Dialog>
  );
}
